import React, { useMemo } from 'react';

import getSelectedMediaElementsObjects from 'editor/src/store/design/selector/getSelectedMediaElementsObjects';
import { isTextElement } from 'editor/src/store/design/util/elementTypeGuards';
import setSidebarActiveTabByNameOperation from 'editor/src/store/editorModules/sidebar/operation/setSidebarActiveTabByNameOperation';
import isSidebarTabActive from 'editor/src/store/editorModules/sidebar/selector/isSidebarTabActive';
import { TAB_NAMES } from 'editor/src/store/editorModules/sidebar/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import { RootState } from 'editor/src/store';

import FontColorsTabContent from 'editor/src/component/DesktopSidebar/TabContents/FontColorsTabContent';
import IconFontColor from 'editor/src/component/Icon/IconFontColor';
import { CanShow, MenuItemProps } from 'editor/src/component/Menu/type';
import useMobileMenu from 'editor/src/component/useMobileMenu';

import MenuButton, { Props as MenuProps } from './MenuButton';

const DEFAULT_COLOR = '#212121';
const MenuColorButton = MenuButton as React.FC<MenuProps<{ underlineColors: string[] }>>;

export const canShow: CanShow = (state: RootState, { isPersonalizationLocked }: { isPersonalizationLocked: boolean }) =>
  !isPersonalizationLocked;

type Props = Pick<MenuItemProps, 'mode' | 'close' | 'isMobile'>;

function ButtonFontColor({ mode, close, isMobile }: Props) {
  const dispatch = useDispatch();
  const colorMenu = useMobileMenu({ addBackdrop: false, forceHalfScreen: true, flexLayout: true });
  const selectedElements = useSelector(getSelectedMediaElementsObjects);
  const isActive = useSelector((state) => isSidebarTabActive(state, TAB_NAMES.FONT_COLORS));

  const uniqueColors = useMemo(() => {
    if (!selectedElements?.length) {
      return [DEFAULT_COLOR];
    }

    const textElements = selectedElements.filter(isTextElement);
    const colors = [...new Set(textElements.map((element) => element.fill).filter(Boolean))];

    return colors.length ? colors : [DEFAULT_COLOR];
  }, [selectedElements]);

  const handleClick = () => {
    if (isMobile) {
      colorMenu.toggle();
    } else {
      dispatch(setSidebarActiveTabByNameOperation(TAB_NAMES.FONT_COLORS));
      close?.();
    }
  };

  return (
    <>
      <MenuColorButton
        Icon={IconFontColor}
        iconProps={{ underlineColors: uniqueColors }}
        on={isActive}
        mode={mode}
        handleClick={handleClick}
        i18nKey="editor-font-color"
        className="cy-button-font-color"
      />
      {colorMenu.render(<FontColorsTabContent closeMenuCb={colorMenu.close} />)}
    </>
  );
}

export default React.memo(ButtonFontColor);
